<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Lista opiekunów</h4>
        </template>
        <template v-else>
          <h4>Dane opiekunów:
            <p class="mt-1 mb-0 patient-name">
              {{ patient.name + ' ' + patient.last_name }}
            </p>
          </h4>
        </template>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddGuardianPopup"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj opiekuna
        </d-button>
      </d-col>
    </d-row>

    <d-card class="w-100" style="box-shadow: none;">
      <table class="table mt-0 mb-0 w-100">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Typ opiekuna</th>
            <th scope="col" class="border-0">Imię i nazwisko</th>
            <th scope="col" class="border-0">E-mail</th>
            <th scope="col" class="border-0">Telefon</th>
            <th scope="col" class="border-0">Adres</th>
            <th scope="col" class="border-0 text-center">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(guardian, guardianIndex) of guardians"
            :key="'guardian-' + guardianIndex">
            <td class="align-middle">
              {{ getGuardianTypeName(guardian.type) }}
            </td>
            <td class="align-middle">
              {{ guardian.name }} {{ guardian.last_name }}
            </td>
            <td class="align-middle">
              {{ guardian.email }}
            </td>
            <td class="align-middle">
              {{ guardian.phone }}
            </td>
            <td class="align-middle">
              {{ getGuardianAddress(guardian) }}
            </td>
            <td
              class="align-middle text-center"
              style="width: 130px;">
              <d-button
                theme="primary"
                :disabled="removeInProgress"
                @click.prevent="showEditGuardianPopup(guardian)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeGuardian(guardian.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr>
            <td
              v-if="!guardians.length"
              colspan="6"
              class="text-center">
              Brak opiekunów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!guardians" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <patients-form-guardians-popup
      :guardian="editGuardian"
      :is-visible="isShowGuardianPopup"
      :on-close-popup="handleCloseGuardianPopup"
      :on-submit-callback="loadItemsData"
      :patient-id="patient.id" />
  </div>
</template>

<script>
import Vue from 'vue';
import ListUtils from '@/utils/ListUtils.js';
import PatientsFrmGuardiansPopup from '@/components/forms/patients/PatientsFormGuardiansPopup';
import GuardianTypes from '@/data/guardian-types.js';

export default {
  name: 'patients-form-guardians',
  props: {
    isInPopup: {
      type: Boolean,
      default: false
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    patient: {
      type: Object,
      required: true
    }
  },
  components: {
    'patients-form-guardians-popup': PatientsFrmGuardiansPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    guardianTypes () {
      return GuardianTypes;
    }
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.loadItemsData();
      }
    }
  },
  data () {
    return {
      editGuardian: null,
      guardians: [],
      isShowGuardianPopup: false,
      loaded: false,
      loadError: false,
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      ListUtils.loadItemsData(this, {
        endpoint: '/patients/guardians/items/' + this.patient.id,
        listField: 'guardians',
        pagination: false
      });
    },
    removeGuardian (guardianID) {
      Vue.set(this, 'removeInProgress', true);

      ListUtils.removeItem(this, {
        endpointBase: '/patients/guardians/delete/',
        id: guardianID,
        successTitle: 'Usunięto opiekuna',
        successText: 'Wybrany opiekun został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tego opiekuna?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie opiekuna nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          Vue.set(this, 'removeInProgress', false);

          this.loadItemsData();
        },
        cancelAction: () => {
          Vue.set(this, 'removeInProgress', false);
        }
      });
    },
    getGuardianTypeName (guardianType) {
      let guardianFound = this.guardianTypes.find(item => item.value === guardianType);
      if (!guardianFound || !guardianFound.name) {
        return 'brak';
      }

      return guardianFound.name;
    },
    getGuardianAddress (guardian) {
      let output = '';
      if (guardian.city) {
        output += guardian.city;
      }

      if (guardian.street) {
        output += (', ul. ' + guardian.street);
      }

      if (guardian.number_home) {
        output += (' ' + guardian.number_home);
      }

      if (guardian.number_local) {
        output += (' / ' + guardian.number_local);
      }
      return output;
    },
    showEditGuardianPopup (guardian) {
      Vue.set(this, 'editGuardian', guardian);
      Vue.set(this, 'isShowGuardianPopup', true);
    },
    showAddGuardianPopup () {
      Vue.set(this, 'editGuardian', null);
      Vue.set(this, 'isShowGuardianPopup', true);
    },
    handleCloseGuardianPopup () {
      Vue.set(this, 'isShowGuardianPopup', false);
      Vue.set(this, 'editGuardian', null);
    }
  }
}
</script>
